import type { AdtraceInitOptions, Env, LogLevel } from "src/types/adtrace";
import {
  isAdtraceAllowed,
  isAdtracePwaAllowed,
} from "../features/analytics/adtraceEnabled";
import { AppDispatch } from "src/store";
import { adtraceEventThunk } from "../store/thunks/analyticsThunk";
import { AdtraceEventType } from "src/types/api/analytics.types";

let inited = false;
let AdtraceRef: any | null = null;

function getEnv<K extends string>(key: K) {
  return import.meta.env[key as any] as string | undefined;
}

function getAdtraceInstance() {
  return AdtraceRef || (window as any).Adtrace || null;
}

// Just once init
export async function initAdtrace(overrides: Partial<AdtraceInitOptions> = {}) {
  if (typeof window === "undefined") return;

  // HARD STOP if not PWA/TWA
  if (!isAdtraceAllowed()) return;

  if (inited || (window as any).__adtraceInited) return;

  const Adtrace =
    (window as any).Adtrace ?? (await import("web-adtrace")).default;
  AdtraceRef = Adtrace;

  const appToken = getEnv("VITE_ADTRACE_APP_TOKEN");
  const environment =
    (getEnv("VITE_ADTRACE_ENV") as Env) ||
    (import.meta.env.PROD ? "production" : "sandbox");

  if (!appToken) {
    if (!import.meta.env.PROD) {
      console.warn(
        "[Adtrace] Missing VITE_ADTRACE_APP_TOKEN; not initialized."
      );
    }
    return;
  }

  const opts: AdtraceInitOptions = {
    appToken,
    environment,
    namespace: getEnv("VITE_ADTRACE_NAMESPACE"),
    logLevel:
      (getEnv("VITE_ADTRACE_LOG_LEVEL") as LogLevel) ||
      (environment === "sandbox" ? "verbose" : "error"),
    attributionCallback: (_e, attribution) => {
      if (!import.meta.env.PROD) {
        console.log("[Adtrace] Attribution changed", attribution);
      }
    },
    ...overrides,
  };

  Adtrace.initSdk(opts);
  inited = true;
  (window as any).__adtraceInited = true;
}

export function setAdtraceExternalId(externalDeviceId?: string) {
  if (!externalDeviceId) return;
  const A = AdtraceRef || (window as any).Adtrace;
  if (!A) return;

  if (typeof A.setExternalDeviceId === "function") {
    A.setExternalDeviceId(String(externalDeviceId));
    return;
  }

  if (typeof A.addSessionCallbackParameter === "function") {
    A.addSessionCallbackParameter(
      "external_device_id",
      String(externalDeviceId)
    );
  }
}

export async function initAdtraceWithExternalId(externalDeviceId?: string) {
  await initAdtrace();
  setAdtraceExternalId(externalDeviceId);
}

/**
 * Event Tracking
 * - These helpers ensure SDK is initialized before tracking.
 */

export type TrackAdtraceEventParams = {
  eventToken: string;
  revenue?: number;
  currency?: string;
  deduplicationId?: string;
  callbackParams?: { key: string; value: string }[];
  partnerParams?: { key: string; value: string }[];
  valueParams?: { key: string; value: string }[];
};

/** Fire-and-forget. Returns the SDK promise (if available). */
export async function trackAdtraceEvent(
  params: TrackAdtraceEventParams,
  dispatch: AppDispatch
) {
  if (typeof window === "undefined") return;
  if (!isAdtraceAllowed()) return;

  // Must init first (per Adtrace docs)
  await initAdtrace();

  //TWA
  if (localStorage.getItem("gps_ad_id")) {
    const gpsAdId = localStorage.getItem("gps_ad_id");
    // Add gps_ad_id to event parameters if it exists
    const eventTwaParams: AdtraceEventType = {
      device_id: gpsAdId ? gpsAdId : undefined,
      event_token: params.eventToken,
    };

    dispatch(adtraceEventThunk(eventTwaParams)).unwrap();
    return;
  }

  //PWA
  if (isAdtracePwaAllowed()) {
    const A = getAdtraceInstance();
    if (!A || typeof A.trackEvent !== "function") {
      if (!import.meta.env.PROD)
        console.warn("[Adtrace] trackEvent not available");
      return;
    }
    // Adtrace.trackEvent returns a Promise (per docs)
    return A.trackEvent(params);
  }
}

/**
 * Use this before navigation/redirect when you *prefer* to give the SDK a moment.
 * It will never block UX longer than timeoutMs.
 */
export async function trackAdtraceEventWithTimeout(
  params: TrackAdtraceEventParams,
  dispatch: AppDispatch,
  timeoutMs = 1200
) {
  try {
    await Promise.race([
      trackAdtraceEvent(params, dispatch),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("adtrace_event_timeout")), timeoutMs)
      ),
    ]);
  } catch {
    // swallow: SDK queues events internally; proceed with navigation
  }
}
